import type { AddOnKey, EntityPreference } from '@invisoutlet/shared/types';

// ─── Add-on metadata ──────────────────────────────────────────────────────────

/** Short display label used in badge lists and table cells. */
export const ADD_ON_LABELS: Record<AddOnKey, string> = {
  smsAlerts: 'SMS Alerts',
  apiAccess: 'API Access',
  advancedAnalytics: 'Advanced Analytics',
  historicalData: 'Historical Data',
  customBranding: 'Custom Branding',
  stayCalendar: 'Stay Calendar',
  buildingAISummary: 'AI Building Summary',
};

/** Short badge label — used where horizontal space is tight (e.g. entity list). */
export const ADD_ON_SHORT_LABELS: Record<AddOnKey, string> = {
  smsAlerts: 'SMS',
  apiAccess: 'API',
  advancedAnalytics: 'Analytics',
  historicalData: 'History',
  customBranding: 'Branding',
  stayCalendar: 'Stays',
  buildingAISummary: 'AI Summary',
};

/** One-sentence description shown in the add-on toggle UI. */
export const ADD_ON_DESCRIPTIONS: Record<AddOnKey, string> = {
  smsAlerts: 'Send SMS notifications when alert rules trigger.',
  apiAccess: 'Allow programmatic access via REST API.',
  advancedAnalytics: 'AI-powered usage and trend analytics.',
  historicalData: 'Query and export historical sensor readings.',
  customBranding: 'White-label the portal with custom logo and colors.',
  stayCalendar:
    'Track guest bookings per property and detect trespassing when occupancy is detected outside a booked stay.',
  buildingAISummary:
    'AI-generated natural language summary of selected buildings on the Building Overview page, powered by Google Gemini.',
};

/** Canonical display order for add-ons across all UI surfaces. */
export const ADD_ON_KEYS: AddOnKey[] = [
  'smsAlerts',
  'apiAccess',
  'advancedAnalytics',
  'historicalData',
  'customBranding',
  'stayCalendar',
  'buildingAISummary',
];

// ─── Temperature unit options ─────────────────────────────────────────────────

export interface TemperatureUnitOption {
  value: EntityPreference['temperatureUnit'];
  label: string;
}

export const TEMPERATURE_UNIT_OPTIONS: TemperatureUnitOption[] = [
  { value: '°F', label: 'Fahrenheit (°F)' },
  { value: '°C', label: 'Celsius (°C)' },
];

// ─── Notification mode options ────────────────────────────────────────────────

export interface NotificationModeOption {
  value: EntityPreference['notification'];
  label: string;
  description: string;
}

export const NOTIFICATION_MODE_OPTIONS: NotificationModeOption[] = [
  {
    value: 'auto',
    label: 'Auto',
    description: 'Alerts trigger automatically when rules are matched.',
  },
  {
    value: 'manual',
    label: 'Manual',
    description: 'Rules exist but notifications must be manually armed.',
  },
  {
    value: 'off',
    label: 'Off',
    description: 'No notifications sent regardless of rule state.',
  },
];
